/**
 * Cart checkout row: Check out full width, the wallet buttons sharing the line beneath.
 *
 * Shopify's <shopify-accelerated-checkout-cart> renders every wallet in its
 * `wallet-configs` attribute inside a CLOSED shadow root, one row per wallet. Nothing set
 * on the host — grid, flex, flex-direction, or any of the documented
 * --shopify-accelerated-checkout-* properties — moves those rows, and the container stays
 * stacked even when it is 760px wide. A plain <div> appended to it renders 0x0, because it
 * matches no slot.
 *
 * So the rows are not rearranged: each wallet is given a container of its OWN. The element
 * is replaced with one host per wallet, each carrying a single-entry `wallet-configs`, and
 * those hosts are ordinary elements that a flex row can lay out. Shopify still builds and
 * owns every button; all this changes is how many containers it builds them in.
 *
 * It also drops any wallet listed in HIDE before building the hosts.
 *
 * An earlier version of this file solved the layout problem by deleting every wallet
 * except Apple Pay, which is why the drawer showed only one.
 *
 * Re-runs after every Section Rendering morph of the cart, and marks what it has already
 * handled so replacing an element cannot retrigger itself.
 */
const DONE = 'hoomanWalletSplit';
const ROW_CLASS = 'hooman-wallet-row';

/**
 * Wallets to hide. Chris, 2026-09-22: "can we remove shop from view and replace that with
 * google pay?" — so Shop Pay comes out and Google Pay takes its place beside Apple Pay.
 *
 * This only changes which buttons are OFFERED in the cart. Shop Pay is still a payment
 * method at the checkout itself; nothing here disables it.
 */
const HIDE = new Set(['shop_pay']);

function split(original) {
  if (original.dataset[DONE]) return;
  original.dataset[DONE] = '1';

  let configs;
  try {
    configs = JSON.parse(original.getAttribute('wallet-configs') || '[]');
  } catch {
    return; /* leave Shopify's own markup alone if it is not what we expect */
  }
  if (!Array.isArray(configs)) return;

  const wanted = configs.filter((config) => !HIDE.has(config.name));

  /* Nothing to hide and nothing to split: leave Shopify's own element exactly as it is. */
  if (wanted.length === configs.length && configs.length < 2) return;

  /* Everything was hidden — drop the row rather than leave an empty container. */
  if (wanted.length === 0) {
    original.remove();
    return;
  }

  const configsToRender = wanted;

  const row = document.createElement('div');
  row.className = ROW_CLASS;

  for (const config of configsToRender) {
    const host = document.createElement('shopify-accelerated-checkout-cart');
    for (const attribute of original.attributes) {
      host.setAttribute(attribute.name, attribute.value);
    }
    host.setAttribute('wallet-configs', JSON.stringify([config]));
    host.dataset[DONE] = '1';
    row.appendChild(host);
  }

  original.replaceWith(row);
}

function sweep(root) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll('.cart__ctas shopify-accelerated-checkout-cart').forEach(split);
}

sweep(document);

if (typeof MutationObserver === 'function') {
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('.cart__ctas shopify-accelerated-checkout-cart')) split(node);
        else sweep(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}
